import React, { useCallback, useMemo, useState } from 'react';
import GitChanges from './GitChanges';
import GitHistory from './GitHistory';
import GitDetailTabs from './GitDetailTabs';
import { gitWords } from '../../services/git/gitSyncStatus';
import { useNotificationStore } from '../../store/notificationStore';

// Loaded on use, like an item's History (ScopeHistoryButton): it brings the contributors.
const service = () => import('../../services/git/scopeChanges');

/**
 * Discard / Restore puts an EXISTING item back to another version of itself.
 * A new item (Discard would delete it) or a deleted one (Restore would
 * recreate it) is not offered — the board or the documents tree is the place
 * for that, as in an item's own History.
 */
export const canPutBack = (item, mode) =>
  item?.group !== 'Other' && typeof item?.path === 'string' && (mode === 'discard' ? item.kind === 'modified' : item.kind === 'modified' || item.kind === 'added');

/**
 * Changes and History for the selected boards & documents repository
 * (owner, 05.10.2026) — the same tabs, rows and diffs a workspace has: a new
 * task, a changed task with its fields and the text diff, a moved document.
 *
 * @param {{ scope: {key, name, git}, busy: boolean, waiting?: number }} props
 */
export default function GitScopeChanges({ scope, busy, waiting = 0 }) {
  const [putBacks, setPutBacks] = useState(0);
  // GitChanges / GitHistory speak "workspace": the scope in that shape.
  const target = useMemo(() => ({ id: scope.key, name: scope.name, git: scope.git }), [scope.key, scope.name, scope.git]);
  const words = gitWords(target);

  // Keyed on the scope's key and link, not the object: a list must not reload because a parent re-rendered.
  const ref = useMemo(() => ({ key: scope.key, name: scope.name, git: scope.git }), [scope.key, scope.name, scope.git]);
  const loadChanges = useCallback(async () => (await service()).loadScopeChanges(ref), [ref]);
  const loadHistory = useCallback(async () => (await service()).loadScopeHistory(ref), [ref]);
  const loadItems = useCallback(async (_, commit) => (await service()).loadScopeCommitItems(ref, commit), [ref]);

  const putBack = async (_, path, content, mode) => {
    try {
      await (await service()).putBackScopeItem(ref, path, content);
      useNotificationStore.getState().success(
        mode === 'discard' ? `Back to how it was at ${words.last}.` : `The version is back in Apiboo. ${words.action} records it.`,
        mode === 'discard' ? 'Change discarded' : 'Version restored'
      );
      setPutBacks((n) => n + 1);
    } catch (err) {
      useNotificationStore.getState().error(err?.message || 'Could not put that version back.', 'Nothing changed');
      throw err;
    }
  };

  return (
    <GitDetailTabs
      waiting={waiting}
      changes={
        <GitChanges key={`c${putBacks}`} workspace={target} onLoadChanges={loadChanges} onPutBack={putBack} canPutBack={canPutBack} syncing={busy} />
      }
      renderHistory={(onCount) => (
        <GitHistory
          key={`h${putBacks}`}
          workspace={target}
          onPutBack={putBack}
          syncing={busy}
          loadHistory={loadHistory}
          loadItems={loadItems}
          canPutBack={canPutBack}
          onCount={onCount}
        />
      )}
    />
  );
}
